import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  generateWeeklyDigestHTML,
  generateStaffInviteHTML,
  generateWelcomeEmailHTML,
  generateEngagementEmailHTML,
  generateConversionEmailHTML,
  type WeeklyDigestProps,
  type StaffInviteProps,
  type WelcomeEmailProps,
  type EngagementEmailProps,
  type ConversionEmailProps,
} from "@/lib/email-templates";

type Mode =
  | "digest-homecare"
  | "digest-grouphome"
  | "invite-manager"
  | "invite-staff"
  | "welcome"
  | "engagement-no-hot"
  | "engagement-hot"
  | "conversion-starter"
  | "conversion-growth";

const digestHomecare: WeeklyDigestProps = {
  ownerName: "Eileen",
  agencyName: "Sunrise Home Care LLC",
  segment: "homecare",
  weekRange: "April 7 – April 13, 2026",
  totalLeads: 14,
  hotLeads: 3,
  emergencyLeads: 1,
  callsBooked: 2,
  pipelineValue: "$18,400",
  avgLeadScore: 67,
  topReferralSource: "Hospital Discharge Planner",
  teamLogins: 3,
  teamTotal: 5,
  staleLeads: 1,
  topSpecialty: "Alzheimer's / Dementia",
  unreadAlerts: 2,
  ctaUrl: "https://app.legacygrowthengine.com/dashboard",
  unsubscribeUrl: "https://app.legacygrowthengine.com/unsubscribe",
  segment_label: "leads",
  beds_available: null,
  placements_this_week: null,
};

const digestGrouphome: WeeklyDigestProps = {
  ...digestHomecare,
  segment: "grouphome",
  agencyName: "Bright Path Group Home",
  segment_label: "inquiries",
  topSpecialty: "Adults with IDD",
  beds_available: 4,
  placements_this_week: 1,
};

const inviteStaff: StaffInviteProps = {
  inviteeName: "Marcus",
  inviteeEmail: "marcus@sunrisehomecare.com",
  inviterName: "Eileen Teckham",
  agencyName: "Sunrise Home Care LLC",
  agencySegment: "homecare",
  role: "staff",
  personalMessage:
    "Welcome to the team Marcus. Please accept this invite so you can start managing your assigned leads.",
  inviteUrl: "https://app.legacygrowthengine.com/invite/accept?token=abc123",
  inviteExpiry: "72 hours",
  supportEmail: "support@legacygrowthengine.com",
  unsubscribeUrl: "https://app.legacygrowthengine.com/unsubscribe",
};

const inviteManager: StaffInviteProps = {
  ...inviteStaff,
  role: "manager",
  inviteeName: "Priya",
  inviteeEmail: "priya@sunrisehomecare.com",
};

const welcomeMock: WelcomeEmailProps = {
  ownerName: "Eileen",
  agencyName: "Sunrise Home Care LLC",
  segment: "homecare",
  plan: "Growth",
  trialEndDate: "April 28, 2026",
  trialDaysRemaining: 14,
  dashboardUrl: "https://app.legacygrowthengine.com/dashboard",
  chatbotEmbedUrl: "https://app.legacygrowthengine.com/dashboard/chatbot",
  onboardingUrl: "https://app.legacygrowthengine.com/onboarding",
  supportEmail: "support@legacygrowthengine.com",
  unsubscribeUrl: "https://app.legacygrowthengine.com/unsubscribe",
  segment_label: "leads",
  isOnboardingComplete: false,
};

const engagementNoHotLeadMock: EngagementEmailProps = {
  ownerName: "Eileen",
  agencyName: "Sunrise Home Care LLC",
  segment: "homecare",
  plan: "Growth",
  trialDaysRemaining: 11,
  trialEndDate: "April 28, 2026",
  hasHotLead: false,
  hotLeadName: null,
  hotLeadScore: null,
  hotLeadSpecialty: null,
  hotLeadZip: null,
  totalLeadsCaptured: 4,
  avgScore: 58,
  bookingUrl: "https://calendly.com/eileen",
  referralOutreachUrl: "https://app.legacygrowthengine.com/dashboard/outreach",
  dashboardUrl: "https://app.legacygrowthengine.com/dashboard",
  supportEmail: "support@legacygrowthengine.com",
  unsubscribeUrl: "https://app.legacygrowthengine.com/unsubscribe",
  segment_label: "leads",
};

const engagementHotLeadMock: EngagementEmailProps = {
  ...engagementNoHotLeadMock,
  trialDaysRemaining: 12,
  hasHotLead: true,
  hotLeadName: "Margaret T.",
  hotLeadScore: 97,
  hotLeadSpecialty: "Alzheimer's / Dementia",
  hotLeadZip: "20904",
  totalLeadsCaptured: 7,
  avgScore: 72,
};

const conversionStarterMock: ConversionEmailProps = {
  ownerName: "Eileen",
  agencyName: "Sunrise Home Care LLC",
  segment: "homecare",
  currentPlan: "Starter",
  trialDaysRemaining: 3,
  trialEndDate: "April 28, 2026",
  totalLeadsCaptured: 14,
  hotLeadsTotal: 3,
  callsBooked: 2,
  estimatedPipelineValue: "$18,400",
  teamMembersAdded: 2,
  referralEmailsSent: 5,
  isOnboardingComplete: true,
  upgradeUrl: "https://app.legacygrowthengine.com/settings/billing",
  growthPlanPrice: "$197",
  starterPlanPrice: "$97",
  dashboardUrl: "https://app.legacygrowthengine.com/dashboard",
  supportEmail: "support@legacygrowthengine.com",
  unsubscribeUrl: "https://app.legacygrowthengine.com/unsubscribe",
  segment_label: "leads",
};

const conversionGrowthMock: ConversionEmailProps = {
  ...conversionStarterMock,
  currentPlan: "Growth",
};

export default function EmailPreview() {
  const [mode, setMode] = useState<Mode>("welcome");

  const html = useMemo(() => {
    switch (mode) {
      case "digest-homecare":
        return generateWeeklyDigestHTML(digestHomecare);
      case "digest-grouphome":
        return generateWeeklyDigestHTML(digestGrouphome);
      case "invite-manager":
        return generateStaffInviteHTML(inviteManager);
      case "invite-staff":
        return generateStaffInviteHTML(inviteStaff);
      case "welcome":
        return generateWelcomeEmailHTML(welcomeMock);
      case "engagement-no-hot":
        return generateEngagementEmailHTML(engagementNoHotLeadMock);
      case "engagement-hot":
        return generateEngagementEmailHTML(engagementHotLeadMock);
      case "conversion-starter":
        return generateConversionEmailHTML(conversionStarterMock);
      case "conversion-growth":
        return generateConversionEmailHTML(conversionGrowthMock);
    }
  }, [mode]);

  const tabs: { id: Mode; label: string }[] = [
    { id: "welcome", label: "Onboarding 1 — Welcome" },
    { id: "engagement-no-hot", label: "Onboarding 2 — No Hot Lead" },
    { id: "engagement-hot", label: "Onboarding 2 — Hot Lead" },
    { id: "conversion-starter", label: "Onboarding 3 — Starter" },
    { id: "conversion-growth", label: "Onboarding 3 — Growth" },
    { id: "digest-homecare", label: "Weekly Digest — Home Care" },
    { id: "digest-grouphome", label: "Weekly Digest — Group Home" },
    { id: "invite-manager", label: "Staff Invite — Manager" },
    { id: "invite-staff", label: "Staff Invite — Staff" },
  ];

  return (
    <div className="min-h-screen bg-muted/30 p-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-foreground mb-1">Email Template Preview</h1>
        <p className="text-sm text-muted-foreground mb-4">
          Pixel-accurate preview at 600px (the standard email client width).
        </p>

        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((t) => (
            <Button
              key={t.id}
              variant={mode === t.id ? "default" : "outline"}
              size="sm"
              onClick={() => setMode(t.id)}
            >
              {t.label}
            </Button>
          ))}
        </div>

        <div className="flex justify-center bg-background rounded-lg shadow-sm border p-4">
          <iframe
            title="Email preview"
            srcDoc={html}
            sandbox=""
            style={{ width: 600, height: 1600, border: "1px solid hsl(var(--border))", borderRadius: 8, background: "#F0F2F5" }}
          />
        </div>
      </div>
    </div>
  );
}
